import FetchClient, { type ApiResponse } from "./FetchClient";
import { memoryCache } from "./MemoryCache";

export interface LeadFavorite {
  leadId: string;
  sku: string;
  category: string;
  lowestPrice: number;
  msrp: number;
  onlinePrice: number;
  image: string;
  title: string;
  brand: string;
  lastUpdated: number;
  added: number;
  retailer: string;
}

export interface LeadFavoritesResponse {
  favorites: LeadFavorite[];
  count: number;
}

export interface LeadFavoriteIDsResponse {
  ids: string[]; // Format: "retailer#leadId"
}

// Helper to create composite key
export const makeLeadFavoriteKey = (retailer: string, leadId: string): string => `${retailer}#${leadId}`;

// Helper to parse composite key
export const parseLeadFavoriteKey = (key: string): { retailer: string; leadId: string } => {
  const [retailer, ...rest] = key.split("#");
  return { retailer, leadId: rest.join("#") };
};

// Get all saved leads for a user
export const getLeadFavorites = async (retailer?: string): Promise<LeadFavoritesResponse> => {
  const params = retailer ? `?retailer=${encodeURIComponent(retailer)}` : "";
  const response: ApiResponse<LeadFavoritesResponse> = await FetchClient.get(`/leads-favorites${params}`);
  return response.data;
};

// Get just the IDs of saved leads (lightweight) - returns composite keys "retailer#leadId"
export const getLeadFavoriteIDs = async (retailer?: string): Promise<string[]> => {
  const cacheKey = `leads-favorite-ids:${retailer || "all"}`;
  const cached = memoryCache.get<string[]>(cacheKey);
  if (cached !== null) return cached;

  try {
    const params = retailer ? `?retailer=${encodeURIComponent(retailer)}` : "";
    const response: ApiResponse<LeadFavoriteIDsResponse> = await FetchClient.get(`/leads-favorites/ids${params}`);
    const ids = response.data?.ids ?? [];
    memoryCache.set(cacheKey, ids, 5 * 60 * 1000);
    return ids;
  } catch {
    return [];
  }
};

// Add a lead to favorites
export const addLeadFavorite = async (leadId: string, retailer: string): Promise<void> => {
  await FetchClient.post("/leads-favorites", { leadId, retailer });
  const compositeKey = makeLeadFavoriteKey(retailer, leadId);
  memoryCache.deletePattern(/^leads-favorite-ids:/);
  memoryCache.set(`lead-favorite:${compositeKey}`, true, 5 * 60 * 1000);
};

// Remove a lead from favorites (expects composite key: retailer#leadId)
export const removeLeadFavorite = async (compositeKey: string): Promise<void> => {
  await FetchClient.delete(`/leads-favorites/${encodeURIComponent(compositeKey)}`);
  memoryCache.deletePattern(/^leads-favorite-ids:/);
  memoryCache.set(`lead-favorite:${compositeKey}`, false, 5 * 60 * 1000);
};
